/**
 * contact-panel.tsx — Hồ sơ khách hàng ở cột phải màn Hội thoại.
 *
 * Bố cục bám khối nghiệp vụ SaaS trong BRD §4.5, dựng bằng dữ liệu ĐANG CÓ:
 *  1. Nhận diện — tên (kèm icon mở hồ sơ), chức danh, nhiệt độ mua
 *     + các trường ghim: điện thoại · email · ngày sinh (sửa tại chỗ)
 *  2. Lịch hẹn  — danh sách lịch sắp tới, nút Đặt lịch ngay trong thẻ
 *  3. Ghi chú   — hiển thị lần lượt trong thẻ (ghim trước), nút Thêm
 *  4. Tín hiệu bán hàng — tín hiệu mua, bài toán, đối thủ (AI rút từ hội thoại)
 *  5. Tóm tắt AI
 *  + Evotech (hạng/điểm/tổng chi tiêu) và Đơn hàng — contact-commerce.tsx
 *
 * Khối "Tài khoản dùng thử" (BRD §4.5.1) CHƯA dựng được: cần telemetry sản phẩm
 * (ADM-13), backend chưa có nguồn dữ liệu này.
 */
import { useState } from 'react'
import { noteMeta, noteTimeOf, sortNotes } from '@/lib/note-meta'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  Cake, CalendarClock, Flame, Mail, PanelRightOpen,
  Phone, Pin, Plus, Radar, Snowflake, Sparkles, StickyNote, Swords, TrendingUp,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/misc'
import { Separator, ScrollArea } from '@/components/ui/misc'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  useUpdateContact, useCreateNote, useCreateAppointment,
  useNotes, useAppointments, APPOINTMENT_TYPE_LABELS, APPOINTMENT_STATUS_LABELS,
  appointmentStatusVariant,
} from '@/hooks/use-contacts'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { initials, cn } from '@/lib/utils'
import type { ConversationDetail } from '@/hooks/use-conversations'
import { Customer360Dialog } from './customer-360-dialog'
import { EvotechMemberSection, OrdersSection } from './contact-commerce'
import { ContactDetailDrawer } from '@/pages/customers/contact-detail-drawer'
import type { DetailTab } from '@/pages/customers/contact-detail-page'

/** `ai_intent` = nhiệt độ mua. Chỉ báo quan trọng nhất khi bán SaaS. */
const INTENT: Record<string, { icon: typeof Flame; cls: string }> = {
  hot: { icon: Flame, cls: 'bg-destructive/10 text-destructive' },
  warm: { icon: TrendingUp, cls: 'bg-warning/15 text-warning' },
  cold: { icon: Snowflake, cls: 'bg-muted text-muted-foreground' },
}

/** Nhãn dịch nằm ở `convContact:panel.intent.<khoá>` / `panel.sentiment.<khoá>`. */
const SENTIMENT: Record<string, { variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  positive: { variant: 'success' },
  neutral: { variant: 'secondary' },
  negative: { variant: 'destructive' },
}

/** Chuẩn hoá trường Json — backend trả mảng, nhưng có thể null/không phải mảng. */
function asList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()) : []
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return isNaN(d.getTime()) ? null : d.toLocaleDateString(i18n.language || 'vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Ngày + giờ:phút — dùng cho mốc thời gian ghi chú. */
function formatDateTime(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return `${d.toLocaleDateString(i18n.language || 'vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString(i18n.language || 'vi-VN', { hour: '2-digit', minute: '2-digit' })}`
}

export function ContactPanel({ conv }: { conv?: ConversationDetail }) {
  const { t } = useTranslation('convContact')
  const contact = conv?.contact
  const [noteOpen, setNoteOpen] = useState(false)
  const [demoOpen, setDemoOpen] = useState(false)
  const [c360Open, setC360Open] = useState(false)
  // Màn chi tiết khách trượt từ phải (không rời hội thoại); null = đóng.
  const [detailTab, setDetailTab] = useState<DetailTab | null>(null)

  const updateContact = useUpdateContact()
  const createNote = useCreateNote()
  const createAppointment = useCreateAppointment(contact?.id ?? '')
  const { data: notesData } = useNotes(contact?.id)
  const { data: apptData } = useAppointments(contact?.id)

  if (!contact) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
        {t('panel.groupNoContact')}
      </div>
    )
  }

  const name = contact.fullName || conv?.displayName || t('panel.noName')
  const intent = contact.aiIntent ? INTENT[contact.aiIntent] : undefined
  const sentiment = contact.aiSentimentLabel ? SENTIMENT[contact.aiSentimentLabel] : undefined
  const signals = asList(contact.aiSignals)
  const painPoints = asList(contact.aiPainPoints)
  const competitors = asList(contact.aiCompetitors)
  // Lịch hẹn sắp tới: đã lên lịch + chưa qua ngày, gần nhất trước, tối đa 4.
  const todayStr = new Date().toISOString().slice(0, 10)
  const upcomingAppts = (apptData?.appointments ?? [])
    .filter((a) => a.status === 'scheduled' && a.appointmentDate.slice(0, 10) >= todayStr)
    .sort((a, b) =>
      (a.appointmentDate.slice(0, 10) + (a.appointmentTime ?? '')).localeCompare(
        b.appointmentDate.slice(0, 10) + (b.appointmentTime ?? ''),
      ))
    .slice(0, 4)

  // Ghi chú: ghim trước, còn lại mới nhất trước, tối đa 5 (xem hết ở Hồ sơ).
  const notes = sortNotes(notesData?.notes ?? []).slice(0, 5)
  const totalNotes = notesData?.total ?? 0

  const handleBirthdayChange = async (value: string) => {
    try {
      await updateContact.mutateAsync({ id: contact.id, data: { birthday: value || null } })
      toast.success(value ? t('panel.birthdaySaved') : t('panel.birthdayCleared'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const savePinnedField = async (field: 'phone' | 'email', value: string) => {
    try {
      await updateContact.mutateAsync({ id: contact.id, data: { [field]: value } })
      toast.success(field === 'phone' ? t('panel.savedPhone') : t('panel.savedEmail'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  return (
    <>
      <ScrollArea className="h-full">
        <div className="space-y-4 p-4">
          {/* ── 1. Nhận diện ─────────────────────────────────────── */}
          <div className="flex items-start gap-3">
            <Avatar className="h-12 w-12 shrink-0">
              {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} alt={name} />}
              <AvatarFallback>{initials(name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              {/* Dòng 1: tên + "Chi tiết" (mở màn chi tiết khách trượt từ phải, không rời hội thoại). Dòng 2: chức danh + "AI phân tích"
                  — tách hai dòng để tên không bị cắt khi cột hẹp. Nút shrink-0 + whitespace-nowrap: tên co lại (truncate) chứ không phải nút. */}
              <div className="flex items-center gap-1.5">
                <p className="min-w-0 flex-1 truncate text-sm font-semibold" title={name}>{name}</p>
                <button
                  type="button"
                  onClick={() => setDetailTab('overview')}
                  title={t('panel.detailTitle')}
                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <PanelRightOpen className="h-3 w-3" /> {t('panel.detail')}
                </button>
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {contact.jobTitle || t('panel.noJobTitle')}
                </p>
                {/* Chân dung 360°: dựng từ dữ liệu thật + 3 mục do AI phân tích (có chữ kèm icon cho dễ hiểu). */}
                {conv?.id && (
                  <button
                    type="button"
                    onClick={() => setC360Open(true)}
                    title={t('panel.portrait360')}
                    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <Sparkles className="h-3 w-3 text-primary" />
                    {t('panel.aiAnalysis')}
                  </button>
                )}
              </div>
              {intent && (
                <span className={cn('mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', intent.cls)}>
                  <intent.icon className="h-3 w-3" />
                  {t(`panel.intent.${contact.aiIntent}`)}
                </span>
              )}
            </div>
          </div>

          {/* ── 1b. Trường ghim ngay dưới tên: điện thoại · email · ngày sinh.
               SĐT/email sửa được tại chỗ; tin khách nhắn có SĐT/email cũng được
               backend tự bắt điền vào (chỉ khi trường trống). ── */}
          <div className="space-y-1.5 rounded-lg border p-3">
            <EditableRow
              icon={Phone} label={t('panel.phoneLabel')} value={contact.phone}
              placeholder={t('panel.phonePlaceholder')} inputMode="tel"
              onSave={(v) => savePinnedField('phone', v)}
            />
            <EditableRow
              icon={Mail} label={t('common:fields.email')} value={contact.email}
              placeholder={t('panel.emailPlaceholder')} inputMode="email"
              onSave={(v) => savePinnedField('email', v)}
            />
            <div className="flex items-center gap-2">
              <Cake className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="w-[72px] shrink-0 text-[11px] text-muted-foreground">{t('panel.birthday')}</span>
              <input
                key={contact.id}
                type="date"
                defaultValue={contact.birthday?.slice(0, 10) ?? ''}
                onChange={(e) => handleBirthdayChange(e.target.value)}
                className="h-6 min-w-0 flex-1 rounded border bg-transparent px-1.5 text-xs text-foreground [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>
          </div>

          {/* ── 1c. Thành viên Evotech (ẩn khi chưa kết nối) + Đơn hàng ── */}
          <EvotechMemberSection key={`evo-${contact.id}`} contactId={contact.id} />
          <OrdersSection contactId={contact.id} />

          {/* ── 2. Lịch hẹn — nút đặt lịch nằm ngay trong thẻ ────── */}
          <section className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-xs font-semibold">
                <CalendarClock className="h-3.5 w-3.5 text-primary" /> {t('panel.appointments')}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 px-1.5 text-[11px]"
                onClick={() => setDemoOpen(true)}
              >
                <Plus className="h-3 w-3" /> {t('panel.bookShort')}
              </Button>
            </div>
            {upcomingAppts.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                {t('panel.noAppointments')}
              </p>
            ) : (
              upcomingAppts.map((a) => (
                <div key={a.id} className="rounded-md bg-muted/50 px-2.5 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium">
                      {formatDate(a.appointmentDate)}
                      {a.appointmentTime ? ` · ${a.appointmentTime}` : ''}
                    </span>
                    <Badge variant={appointmentStatusVariant(a.status)} className="shrink-0 text-[10px]">
                      {APPOINTMENT_TYPE_LABELS[a.type] ?? APPOINTMENT_STATUS_LABELS[a.status] ?? a.type}
                    </Badge>
                  </div>
                  {a.notes && (
                    <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground" title={a.notes}>
                      {a.notes}
                    </p>
                  )}
                </div>
              ))
            )}
          </section>

          {/* ── 2c. Ghi chú — hiển thị lần lượt ngay trong thẻ ───── */}
          <section className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-xs font-semibold">
                <StickyNote className="h-3.5 w-3.5 text-primary" /> {t('panel.notes')}
                {totalNotes > 0 && <span className="font-normal text-muted-foreground">({totalNotes})</span>}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 px-1.5 text-[11px]"
                onClick={() => setNoteOpen(true)}
              >
                <Plus className="h-3 w-3" /> {t('common:actions.add')}
              </Button>
            </div>
            {notes.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                {t('panel.noNotes')}
              </p>
            ) : (
              notes.map((n) => (
                <div key={n.id} className="rounded-md bg-muted/50 px-2.5 py-1.5">
                  <p className="whitespace-pre-wrap text-xs leading-relaxed">
                    {n.isPinned && <Pin className="mr-1 inline h-3 w-3 text-primary" />}
                    {n.content}
                  </p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {noteMeta(n).author} · {formatDateTime(noteTimeOf(n))}
                  </p>
                  {noteMeta(n).sourceNote && <p className="text-[10px] text-muted-foreground/80">{noteMeta(n).sourceNote}</p>}
                </div>
              ))
            )}
            {totalNotes > notes.length && (
              <button
                type="button"
                onClick={() => setDetailTab('notes')}
                className="block w-full text-right text-[11px] text-primary hover:underline"
              >
                {t('panel.viewAllNotes', { count: totalNotes })}
              </button>
            )}
          </section>

          {/* ── 4. Tín hiệu bán hàng (chỉ hiện khi AI có rút được) ── */}
          {(signals.length > 0 || painPoints.length > 0 || competitors.length > 0) && (
            <section className="space-y-2.5 rounded-lg border p-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold">
                <Radar className="h-3.5 w-3.5 text-primary" /> {t('panel.salesSignals')}
              </p>
              <ChipList label={t('panel.buySignals')} items={signals} cls="bg-success/10 text-success" />
              <ChipList label={t('panel.painPoints')} items={painPoints} cls="bg-warning/15 text-warning" />
              {competitors.length > 0 && (
                <div>
                  <p className="mb-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Swords className="h-3 w-3" /> {t('panel.competitors')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {competitors.map((c) => (
                      <span key={c} className="rounded px-1.5 py-0.5 text-[11px] font-medium bg-destructive/10 text-destructive">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ── 6. Tóm tắt AI ────────────────────────────────────── */}
          {(contact.aiSummary || sentiment) && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="flex items-center gap-1.5 text-xs font-semibold">
                  <Sparkles className="h-3.5 w-3.5 text-primary" /> {t('panel.aiSummary')}
                </p>
                {sentiment && (
                  <Badge variant={sentiment.variant} className="text-[11px]">
                    {t('panel.sentimentLabel', { label: t(`panel.sentiment.${contact.aiSentimentLabel}`) })}
                  </Badge>
                )}
                {contact.aiSummary && (
                  <p className="rounded-lg bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
                    {contact.aiSummary}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </ScrollArea>

      <NoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        pending={createNote.isPending}
        onSubmit={async (content) => {
          await createNote.mutateAsync({ contactId: contact.id, content })
          toast.success(t('panel.noteSaved'))
        }}
      />
      <DemoDialog
        open={demoOpen}
        onOpenChange={setDemoOpen}
        pending={createAppointment.isPending}
        onSubmit={async (date, time, notes) => {
          await createAppointment.mutateAsync({
            contactId: contact.id,
            appointmentDate: date,
            appointmentTime: time || undefined,
            type: 'demo',
            notes: notes || undefined,
          })
          toast.success(t('panel.demoBooked'))
        }}
      />

      {conv?.id && (
        <Customer360Dialog open={c360Open} onOpenChange={setC360Open} convId={conv.id} />
      )}
      <ContactDetailDrawer
        contactId={contact.id}
        open={detailTab !== null}
        onOpenChange={(o) => { if (!o) setDetailTab(null) }}
        initialTab={detailTab ?? 'overview'}
      />
    </>
  )
}

// ── Thành phần phụ ────────────────────────────────────────────────────

/**
 * Dòng thông tin ghim dưới tên, sửa được tại chỗ: gõ xong rời ô (hoặc Enter)
 * là lưu. `key={value}` để giá trị auto-bắt từ chat (realtime) đè vào ô khi
 * người dùng chưa gõ gì.
 */
function EditableRow({
  icon: Icon, label, value, placeholder, inputMode, onSave,
}: {
  icon: typeof Phone; label: string; value?: string | null
  placeholder: string; inputMode: 'tel' | 'email'
  onSave: (v: string) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="w-[72px] shrink-0 text-[11px] text-muted-foreground">{label}</span>
      <input
        key={value ?? ''}
        type="text"
        inputMode={inputMode}
        defaultValue={value ?? ''}
        placeholder={placeholder}
        onBlur={(e) => {
          const v = e.target.value.trim()
          if (v !== (value ?? '')) onSave(v)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        }}
        className="h-6 min-w-0 flex-1 rounded border bg-transparent px-1.5 text-xs text-foreground placeholder:text-muted-foreground/60"
      />
    </div>
  )
}

function ChipList({ label, items, cls }: { label: string; items: string[]; cls: string }) {
  if (items.length === 0) return null
  return (
    <div>
      <p className="mb-1 text-[11px] text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1">
        {items.map((it) => (
          <span key={it} className={cn('rounded px-1.5 py-0.5 text-[11px] font-medium', cls)}>{it}</span>
        ))}
      </div>
    </div>
  )
}

function NoteDialog({
  open, onOpenChange, pending, onSubmit,
}: { open: boolean; onOpenChange: (o: boolean) => void; pending: boolean; onSubmit: (c: string) => Promise<void> }) {
  const { t } = useTranslation('convContact')
  const [content, setContent] = useState('')
  const save = async () => {
    if (!content.trim()) return
    try {
      await onSubmit(content.trim())
      setContent('')
      onOpenChange(false)
    } catch (e) {
      toast.error(apiError(e))
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-base">{t('panel.noteDialogTitle')}</DialogTitle></DialogHeader>
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t('panel.notePlaceholder')}
          className="min-h-[120px]"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
          <Button onClick={save} disabled={pending || !content.trim()}>{t('panel.saveNote')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DemoDialog({
  open, onOpenChange, pending, onSubmit,
}: {
  open: boolean; onOpenChange: (o: boolean) => void; pending: boolean
  onSubmit: (date: string, time: string, notes: string) => Promise<void>
}) {
  const { t } = useTranslation('convContact')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [notes, setNotes] = useState('')
  const save = async () => {
    if (!date) return
    try {
      await onSubmit(date, time, notes)
      setDate(''); setTime(''); setNotes('')
      onOpenChange(false)
    } catch (e) {
      toast.error(apiError(e))
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-base">{t('panel.demoDialogTitle')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground">{t('common:fields.date')}</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t('panel.timeLabel')}</label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t('common:fields.content')}</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('panel.demoPlaceholder')}
              className="mt-1 min-h-[80px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
          <Button onClick={save} disabled={pending || !date}>{t('panel.bookShort')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
